import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  MCPService,
  MODEL_SURFACE_DEFAULT,
  ChatModelId,
  ResolvedSystemPrompt,
} from "../../services/mcpService";
import {
  selectDefaultForSurface,
  createSystemPrompt,
  updateSystemPrompt,
  fetchSystemPrompts,
  SURFACE_CHAT,
} from "../../store/systemPromptSlice";
import { AppDispatch, RootState } from "../../store";
import { fetchModelCatalog, setDebug } from "../../store/aiChatSlice";

import Select from "../../components/ReusableComponents/Select";
interface PromptDebugPanelProps {
  lastEnhancedPrompt?: string;
  modelOverride?: ChatModelId;
  onModelOverrideChange?: (model: ChatModelId) => void;
  persona?: string;
}

type PromptSource = "in-memory" | "db-default" | "hardcoded-fallback";

const SOURCE_LABELS: Record<PromptSource, { label: string; color: string }> = {
  "in-memory": { label: "In-Memory Override", color: "bg-orange-500" },
  "db-default": { label: "DB Default", color: "bg-blue-500" },
  "hardcoded-fallback": { label: "Hardcoded Fallback", color: "bg-gray-500" },
};

const PromptDebugPanel: React.FC<PromptDebugPanelProps> = ({ lastEnhancedPrompt = "", modelOverride = MODEL_SURFACE_DEFAULT, onModelOverrideChange, persona }) => {
  const dispatch = useDispatch<AppDispatch>();
  // Resolve like the chat lambda does — a chat-tagged record wins, otherwise the shared legacy record.
  const dbDefault = useSelector((state: RootState) => selectDefaultForSurface(state, SURFACE_CHAT));
  const editingSharedRecord = dbDefault != null && !(dbDefault.surface ?? "").trim();
  // Admin debug-reasoning toggle: streams the model's per-round reasoning as collapsible <thinking> blocks in the chat response.
  const debugReasoning = useSelector((state: RootState) => state.aiChat.debug);
  // Tier list is server-owned and admin-gated — model names never ship in the bundle.
  const modelCatalog = useSelector((state: RootState) => state.aiChat.modelCatalog);
  const modelTiers = modelCatalog?.tiers ?? [];

  const [showDebugInfo, setShowDebugInfo] = useState(false);
  // Server-resolved prompt view (the prompt is server-authoritative since issue #694 and no longer exists in the bundle) — fetched from the admin-only /mcp/system-prompt endpoint.
  const [serverPrompt, setServerPrompt] = useState<ResolvedSystemPrompt | null>(null);
  const [serverPromptError, setServerPromptError] = useState<string | null>(null);
  // In-memory override state
  const [customSystemPrompt, setCustomSystemPrompt] = useState<string>("");
  const [isEditingSystemPrompt, setIsEditingSystemPrompt] = useState(false);
  // DB default state
  const [dbPromptDraft, setDbPromptDraft] = useState<string>("");
  const [isEditingDbPrompt, setIsEditingDbPrompt] = useState(false);
  const [isSavingDb, setIsSavingDb] = useState(false);
  // Preview toggle
  const [showPreview, setShowPreview] = useState(false);

  const refreshServerPrompt = useCallback(async () => {
    try {
      setServerPromptError(null);
      const resolved = await MCPService.fetchResolvedSystemPrompt(persona);
      setServerPrompt(resolved);
    } catch (err) {
      console.error("Failed to fetch server-resolved system prompt:", err);
      setServerPromptError("Could not load the server-resolved prompt.");
    }
  }, [persona]);

  // Fetch the server view when the panel opens (and when the persona changes while open).
  useEffect(() => {
    if (showDebugInfo) {
      refreshServerPrompt();
    }
  }, [showDebugInfo, refreshServerPrompt]);

  // Tier options come from the server; the chat hook usually has them already
  useEffect(() => {
    if (!modelCatalog) dispatch(fetchModelCatalog());
  }, [dispatch, modelCatalog]);

  // Keep DB draft in sync when the DB record changes
  useEffect(() => {
    if (!isEditingDbPrompt) {
      setDbPromptDraft(dbDefault?.promptText ?? "");
    }
  }, [dbDefault?.promptText, isEditingDbPrompt]);

  const activeSource: PromptSource = MCPService.customSystemPrompt
    ? "in-memory"
    : serverPrompt?.source ?? "hardcoded-fallback";
  const sourceInfo = SOURCE_LABELS[activeSource];

  // Live preview of what would actually be sent: the in-memory override wins, otherwise whatever the server resolved (DB default or hardcoded fallback, persona interpolated).
  const previewPrompt = MCPService.customSystemPrompt || serverPrompt?.resolved || "";

  const handleSaveDbPrompt = async () => {
    setIsSavingDb(true);
    try {
      if (dbDefault?.id) {
        await dispatch(updateSystemPrompt({ id: dbDefault.id, promptText: dbPromptDraft })).unwrap();
      } else {
        // Tag it for chat — an untagged record would become the SHARED default and silently retarget scheduled runs too.
        await dispatch(
          createSystemPrompt({
            promptText: dbPromptDraft,
            isDefault: true,
            surface: SURFACE_CHAT,
            description: "Default chat system prompt",
          })
        ).unwrap();
      }
      // Refresh to pick up changes (server side caches the DB record for ~60s, so the live chat prompt may lag this panel by up to a minute)
      await dispatch(fetchSystemPrompts());
      await refreshServerPrompt();
      setIsEditingDbPrompt(false);
    } catch (err) {
      console.error("Failed to save DB system prompt:", err);
    } finally {
      setIsSavingDb(false);
    }
  };

  return (
    <div className="flex items-center">
      <button
        onClick={() => {
          setShowDebugInfo(!showDebugInfo);
          if (!showDebugInfo) {
            setCustomSystemPrompt(MCPService.customSystemPrompt || "");
          }
        }}
        className={`eai-btn-toolbar w-eai-control-sm px-0 sm:w-auto sm:px-3 ${showDebugInfo ? "eai-chip-active" : ""}`}
      >
        🔍 <span className="hidden sm:inline">Debug</span>
      </button>

      {showDebugInfo && (
        <div className="absolute left-0 right-0 top-full w-full bg-gray-800 text-green-400 p-4 max-h-96 overflow-y-auto rounded-md shadow-lg z-20">
          {/* Reasoning Stream Section */}
          <div className="pb-4 mb-4 border-b border-gray-700">
            <h3 className="text-sm font-bold text-yellow-400 mb-2">🧠 Reasoning Stream:</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={debugReasoning}
                onChange={(e) => dispatch(setDebug(e.target.checked))}
                className="h-4 w-4 accent-orange-500"
              />
              <span className="text-xs text-eai-ink-ghost">
                Stream the model's per-round reasoning as collapsible{" "}
                <code className="text-green-300">&lt;thinking&gt;</code> blocks in the response.
              </span>
            </label>
          </div>

          {/* Model Selector Section */}
          <div className="pb-4 mb-4 border-b border-gray-700">
            <h3 className="text-sm font-bold text-yellow-400 mb-2">🤖 Chat With Model:</h3>
            <div className="flex items-center gap-3">
              <label htmlFor="debug-ai-model" className="text-xs text-eai-ink-ghost">Model:</label>
              {/* Debug panel is dark, so this one overrides the light frame eai-select ships */}
              <Select
                id="debug-ai-model"
                value={modelOverride}
                onChange={(e) => onModelOverrideChange?.(e.target.value)}
                size="sm"
                className="border-gray-600 bg-gray-900 text-white focus:border-blue-500 focus:ring-blue-500"
                chevronClassName="text-eai-ink-faint"
              >
                <option value={MODEL_SURFACE_DEFAULT}>
                  Surface default{serverPrompt?.model ? ` (${serverPrompt.model})` : ""}
                </option>
                {modelTiers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </Select>
            </div>
            <p className="text-xs text-eai-ink-faint mt-2">
              {modelOverride
                ? "Pinned for your messages only — everyone else still runs the surface's model."
                : "Running whatever the chat surface is configured for. Pick a tier to A/B against it."}
            </p>
          </div>

          {/* Active Source Indicator */}
          <div className="pb-4 mb-4 border-b border-gray-700">
            <h3 className="text-sm font-bold text-yellow-400 mb-2">📡 Active System Prompt:</h3>
            <div className="flex flex-wrap gap-2 items-center">
              <span className={`inline-block px-2 py-1 rounded text-xs font-medium text-white ${sourceInfo.color}`}>
                {sourceInfo.label}
              </span>
              <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-gray-700 text-eai-ink-ghost">
                {serverPrompt?.model ? `Model: ${serverPrompt.model}` : "Model: router default"}
              </span>
              <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-gray-700 text-eai-ink-ghost">
                {editingSharedRecord ? "Record: shared" : dbDefault ? "Record: chat" : "Record: none"}
              </span>
            </div>
          </div>

          {/* In-Memory Override Section */}
          <div className="pb-4 mb-4 border-b border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-bold text-yellow-400">⚡ Quick Override (In-Memory):</h3>
              <div className="flex gap-2">
                {isEditingSystemPrompt ? (
                  <>
                    <button
                      onClick={() => {
                        MCPService.customSystemPrompt = customSystemPrompt || null;
                        setIsEditingSystemPrompt(false);
                      }}
                      className="text-xs bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded-[8px]"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setCustomSystemPrompt(MCPService.customSystemPrompt || "");
                        setIsEditingSystemPrompt(false);
                      }}
                      className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded-[8px]"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        if (!MCPService.customSystemPrompt) {
                          // Seed from the server-resolved prompt (the hardcoded fallback no longer exists client-side).
                          setCustomSystemPrompt(serverPrompt?.resolved ?? "");
                        } else {
                          setCustomSystemPrompt(MCPService.customSystemPrompt);
                        }
                        setIsEditingSystemPrompt(true);
                      }}
                      className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded-[8px]"
                    >
                      Edit
                    </button>
                    {MCPService.customSystemPrompt && (
                      <button
                        onClick={() => {
                          MCPService.customSystemPrompt = null;
                          setCustomSystemPrompt("");
                        }}
                        className="text-xs bg-eai-danger-text hover:bg-eai-danger-solid text-white px-3 py-1 rounded-[8px]"
                      >
                        Clear
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            <p className="text-xs text-eai-ink-faint mb-2">
              Highest priority. Not persisted — lost on page refresh.
            </p>
            {isEditingSystemPrompt ? (
              <textarea
                value={customSystemPrompt}
                onChange={(e) => setCustomSystemPrompt(e.target.value)}
                className="w-full bg-gray-900 text-white text-xs font-mono p-2 rounded border border-gray-600 focus:outline-none focus:border-blue-500"
                rows={8}
                placeholder="Enter custom system prompt here... (leave empty to use default)"
              />
            ) : (
              <div className="bg-gray-900 rounded p-2">
                <pre className="text-xs whitespace-pre-wrap font-mono text-eai-ink-ghost">
                  {MCPService.customSystemPrompt || (
                    <span className="text-eai-ink-subtle italic">No in-memory override set.</span>
                  )}
                </pre>
              </div>
            )}
          </div>

          {/* DB Default Section */}
          <div className="pb-4 mb-4 border-b border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-bold text-yellow-400">💾 DB Default Prompt:</h3>
              <div className="flex gap-2">
                {isEditingDbPrompt ? (
                  <>
                    <button
                      onClick={handleSaveDbPrompt}
                      disabled={isSavingDb}
                      className="text-xs bg-green-600 hover:bg-green-500 disabled:bg-green-800 text-white px-3 py-1 rounded-[8px]"
                    >
                      {isSavingDb ? "Saving..." : "Save to DB"}
                    </button>
                    <button
                      onClick={() => {
                        setDbPromptDraft(dbDefault?.promptText ?? "");
                        setIsEditingDbPrompt(false);
                      }}
                      className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded-[8px]"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      if (!dbDefault?.promptText) {
                        // Pre-fill with the server's template ({{persona}}/{{datetime}} intact)
                        setDbPromptDraft(serverPrompt?.template ?? "");
                      }
                      setIsEditingDbPrompt(true);
                    }}
                    className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded-[8px]"
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-eai-ink-faint mb-2">
              Persisted to DynamoDB. Uses template variables: <code className="text-green-300">{"{{persona}}"}</code>, <code className="text-green-300">{"{{datetime}}"}</code>
            </p>
            {editingSharedRecord && (
              <p className="text-xs text-amber-400 mb-2">
                ⚠ Chat has no prompt of its own, so this is the shared record — edits here also change scheduled runs. Give chat its own record on the System Prompts page to separate them.
              </p>
            )}
            {isEditingDbPrompt ? (
              <textarea
                value={dbPromptDraft}
                onChange={(e) => setDbPromptDraft(e.target.value)}
                className="w-full bg-gray-900 text-white text-xs font-mono p-2 rounded border border-gray-600 focus:outline-none focus:border-blue-500"
                rows={10}
                placeholder="Enter DB default system prompt with {{persona}} and {{datetime}} placeholders..."
              />
            ) : (
              <div className="bg-gray-900 rounded p-2">
                <pre className="text-xs whitespace-pre-wrap font-mono text-eai-ink-ghost">
                  {dbDefault?.promptText || (
                    <span className="text-eai-ink-subtle italic">No DB default set. Using hardcoded fallback. Click Edit to create one.</span>
                  )}
                </pre>
              </div>
            )}
          </div>

          {/* Live Preview Section */}
          <div className="pb-4 border-b border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-bold text-yellow-400">👁️ Live Preview (Interpolated):</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded-[8px]"
                >
                  {showPreview ? "Hide" : "Show"}
                </button>
                {showPreview && (
                  <button
                    onClick={() => navigator.clipboard.writeText(previewPrompt)}
                    className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded-[8px]"
                  >
                    Copy
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-eai-ink-faint mb-2">
              What would be sent as the system prompt right now (with variables resolved).
            </p>
            {serverPromptError && (
              <p className="text-xs text-eai-danger-border-strong mb-2">{serverPromptError}</p>
            )}
            {showPreview && (
              <div className="bg-gray-900 rounded p-2">
                <pre className="text-xs whitespace-pre-wrap font-mono text-eai-ink-ghost break-words">{previewPrompt}</pre>
              </div>
            )}
          </div>

          {lastEnhancedPrompt && (
            <div className="pt-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-bold text-white">🔍 Enhanced Prompt Sent to LLM:</h3>
                <button
                  onClick={() => navigator.clipboard.writeText(lastEnhancedPrompt)}
                  className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded-[8px]"
                >
                  Copy
                </button>
              </div>
              <pre className="text-xs whitespace-pre-wrap font-mono break-words">{lastEnhancedPrompt}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromptDebugPanel;
